import type { GeoJsonObject } from 'geojson';
import { geoJSON, GeoJSON as GeoJSONEntity, LatLng, Map, PathOptions, TileLayer as TileLayerEntity } from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { GeoJSON } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { toasting } from 'src/lib/components/Toast.component';
import { fget } from 'src/lib/fetch';
import { ResponseDocument, ResponseListDocuments } from 'src/modules/dashboard/dto/response';
import { RiverEntity } from 'src/modules/dashboard/dto/river/river.entity';
import { selectIsAllAreasSelected, selectSelectedArea, setSelectedArea } from 'src/redux/slices/area.slice';
import { RootState } from 'src/redux/store';

const emptyFeatureCollection = { type: 'FeatureCollection', features: [] } as GeoJsonObject;
const targetRiverStyle: PathOptions = {
  color: '#0070f3',
  weight: 8,
  opacity: 1,
  lineCap: 'round',
  lineJoin: 'round',
};
const targetRiverShadowStyle: PathOptions = {
  color: '#001f48',
  weight: 14,
  opacity: 0.22,
  lineCap: 'round',
  lineJoin: 'round',
  interactive: false,
  className: 'target-river-shadow',
};

export function CheckBoxRegionGeoJSON({
  label = '対象河川',
  checked,
  baseMap,
}: {
  label?: string;
  baseTileLayer: React.RefObject<TileLayerEntity>;
  checked?: boolean;
  baseMap?: React.RefObject<Map>;
}) {
  const dispatch = useDispatch();
  // Handle checkbox
  const eLabel = useRef<HTMLLabelElement>(null);
  const eInputLabel = useRef<HTMLDivElement>(null);
  const eGeoJSON = useRef<GeoJSONEntity>(null);
  const eGeoJSONShadow = useRef<GeoJSONEntity>(null);

  const [inputChecked, setInputChecked] = useState<boolean>(!!checked);
  const [riverGeoJsonData, setRiverGeoJsonData] = useState<GeoJsonObject[]>([]);

  const selectedAreaCoordinates = useSelector((state: RootState) => state.areaReducer.areaCoordinates);
  const selectedArea = useSelector(selectSelectedArea);
  const isAllAreasSelected = useSelector(selectIsAllAreasSelected);
  const selectedAreaName = selectedArea?.AREA_NAME?.S;

  const changeCheckingStatus = (check: boolean) => {
    if (check) {
      eInputLabel.current?.classList.add('text-white', 'bg-blue-500');
      eInputLabel.current?.classList.remove('text-blue-500');
    } else {
      eInputLabel.current?.classList.add('text-blue-500');
      eInputLabel.current?.classList.remove('text-white', 'bg-blue-500');
    }
  };

  useEffect(() => {
    changeCheckingStatus(inputChecked);
  }, [inputChecked]);

  useEffect(() => {
    if (!selectedAreaName && !isAllAreasSelected) {
      setRiverGeoJsonData([]);
      dispatch(setSelectedArea({ selectedRiverNames: [], selectedRiverBounds: undefined }));
      return;
    }

    setRiverGeoJsonData([]);
    dispatch(setSelectedArea({ selectedRiverNames: [], selectedRiverBounds: undefined }));

    const fetchData = async () => {
      try {
        const riverEndpoint = isAllAreasSelected
          ? '/api/rivers'
          : `/api/riverByArea?AREA_NAME=${encodeURIComponent(selectedAreaName || '')}`;
        const riverResponse = await fget<string, ResponseListDocuments<RiverEntity>>(riverEndpoint);
        const rivers = riverResponse?.[1]?.Items || [];

        const riverNames = rivers
          .map((river: ResponseDocument<RiverEntity>) => river.RIVER_NAME?.S || '')
          .filter(Boolean);

        const geoJsonDataList = rivers
          .map((river: ResponseDocument<RiverEntity>) => river.COORDINATE_DATA?.S)
          .filter((value): value is string => Boolean(value));

        // get presigned url
        const getPresignedUrl = async (key: string | undefined) => {
          if (!key) return;
          const res = await fget<string, { url: string }>(`/api/s3/get-geojson-url?key=${key}`);
          return res?.[1]?.url;
        };

        const geoJsonUrlList = await Promise.all(geoJsonDataList?.map((key) => getPresignedUrl(key)) || []);

        // get geojson data
        const geoJsonData = (
          await Promise.all(
            geoJsonUrlList?.map(async (url, i) => {
              if (!url) return;
              const res = await fetch(url);
              try {
                const data = await res.json();

                return data;
              } catch (e) {
                return null;
              }
            }) || [],
          )
        ).filter((value): value is GeoJsonObject => Boolean(value));

        setRiverGeoJsonData(geoJsonData);

        const riverLayer = geoJSON({ type: 'FeatureCollection', features: [] } as GeoJsonObject);
        geoJsonData.forEach((data) => {
          riverLayer.addData(data);
        });

        const bounds = riverLayer.getBounds();
        dispatch(
          setSelectedArea({
            selectedRiverNames: riverNames,
            selectedRiverBounds: bounds.isValid()
              ? [
                  [bounds.getSouth(), bounds.getWest()],
                  [bounds.getNorth(), bounds.getEast()],
                ]
              : undefined,
          }),
        );
      } catch (e) {
        console.error(e);
        toasting({ children: '地図データの取得に失敗しました', containerProps: { className: 'border-red-600' } });
      }
    };
    void fetchData();
  }, [dispatch, isAllAreasSelected, selectedAreaName]);

  useEffect(() => {
    eGeoJSON.current?.clearLayers();
    eGeoJSONShadow.current?.clearLayers();
    if (inputChecked) {
      riverGeoJsonData.forEach((data) => {
        eGeoJSONShadow.current?.addData(data);
        eGeoJSON.current?.addData(data);
      });
    }
  }, [inputChecked, riverGeoJsonData]);

  useEffect(() => {
    const [lat, lng] = selectedAreaCoordinates.split(',');
    if (isNaN(parseFloat(lat)) || isNaN(parseFloat(lng))) return;

    const cvtSelectedAreaCoordinates: LatLng = new LatLng(parseFloat(lat), parseFloat(lng));
    baseMap?.current?.setView(cvtSelectedAreaCoordinates, 12);
  }, [selectedAreaCoordinates]);

  return (
    <>
      <div className="flex items-center" ref={eInputLabel}>
        <input
          id={label}
          type="checkbox"
          checked={inputChecked}
          onChange={(e) => {
            setInputChecked(e.target.checked);
            const [lat, lng] = selectedAreaCoordinates.split(',');
            if (isNaN(parseFloat(lat)) || isNaN(parseFloat(lng))) return;

            const cvtSelectedAreaCoordinates: LatLng = new LatLng(parseFloat(lat), parseFloat(lng));
            baseMap?.current?.setView(cvtSelectedAreaCoordinates, 12);
          }}
          className="hidden h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
        />
        <label
          htmlFor={label}
          className="flex-1 select-none border-2 border-blue-500 p-2 text-center text-sm font-medium text-inherit md:p-3"
          ref={eLabel}>
          {label}
        </label>
      </div>

      {inputChecked && (
        <>
          <GeoJSON ref={eGeoJSONShadow} data={emptyFeatureCollection} style={targetRiverShadowStyle} />
          <GeoJSON ref={eGeoJSON} data={emptyFeatureCollection} style={targetRiverStyle} />
        </>
      )}
    </>
  );
}
